import { enrichSearchModel } from '@aviv-search/refiner/full';
import { mapSearchModelToSearchServiceModel } from '@aviv-search/transformers';
import { LocationType } from '@aviv-search/types';
import { Order } from '@search/core/brands/models/Order';
import { isServer } from '@search/core/helpers/isServer';
import { fetchFeatureFlags } from '@search/core/services/featureFlags/fetchFeatureFlags';
import { PaginationExceededError } from '@search/core/services/searchData/paginationExceededError';
import { createLogger } from '../../helpers/createLogger';
import { getRouterProps } from '../../helpers/getRouterProps';
import { fetchSeoHeadInfos } from '../../services/headInfos/fetchSeoHeadInfos';
import { fetchSerpData } from '../../services/serpData';
const defaultAppProps = {
    searchModel: undefined,
    searchServiceModel: undefined,
    seoModel: undefined,
    page: 1,
    order: Order.Default,
    searchIndex: 0,
    totalCount: 0,
    topPositionId: undefined,
    pageData: {
        distributionType: '',
        estateType: '',
        locations: [],
    },
    classifieds: [],
    enlargedClassifieds: [],
    classifiedsData: {},
    suggestedClassifieds: [],
    enrichedClassifiedsData: {},
    linkboxes: [],
    breadcrumb: [],
    hubContent: {},
    featureFlags: {},
    apiError: true,
    gone: false,
    isLoading: true,
    appConfig: {
        loggerName: 'app_serp',
        technicalUrl: '',
        searchEndpoints: {
            search: '',
            enlargement: '',
            effect: undefined,
        },
        countMode: 'default',
        tracking: {
            initiative: 'wl_serp',
        },
    },
};
const defaultValues = {
    pageProps: defaultAppProps,
    headInfos: {},
};
export const getInitSerpData = async ({ searchParams, stage, brand, pageSize, bffUri, language, consideredFeatureFlags, defaultOrder, defaultPlaceId, appConfig, numberOfSuggestions, userId, }) => {
    const logger = createLogger(appConfig.loggerName);
    try {
        const featureFlags = await fetchFeatureFlags(consideredFeatureFlags, bffUri, userId);
        const routerProps = await getRouterProps({
            searchParams,
            language,
            bffUri,
            defaultOrder,
            defaultPlaceId,
        });
        if ('gone' in routerProps) {
            return {
                seoInitialProps: {
                    gone: routerProps.gone,
                },
            };
        }
        if ('permanentRedirect' in routerProps) {
            return {
                seoInitialProps: {
                    permanentRedirect: routerProps.permanentRedirect,
                },
            };
        }
        if ('searchModel' in routerProps) {
            const { searchModel, searchServiceModel, seoModel, order, page } = routerProps;
            if ((searchModel?.locations?.length ?? 0) === 0) {
                // prevent calling services with broken model
                return defaultValues;
            }
            let enrichedSearchModel = searchModel;
            let enrichedSearchServiceModel = searchServiceModel;
            const needEnrichment = searchModel?.locations?.some((l) => l.type === LocationType.COMMUTE);
            // Check left here to prevent unnecesary enrichment of SearchServiceModel (could lead to SEO difference with current behavior, to elaborate with them)
            if (needEnrichment) {
                const isServerSide = isServer();
                const model = await enrichSearchModel(searchModel, stage, brand, isServerSide);
                if (model) {
                    enrichedSearchModel = model;
                    enrichedSearchServiceModel = mapSearchModelToSearchServiceModel(enrichedSearchModel);
                }
            }
            const [[searchData, pageData, linkboxes, breadcrumb, content], headInfos] = await Promise.all([
                fetchSerpData({
                    searchModel: enrichedSearchModel,
                    searchServiceModel: enrichedSearchServiceModel,
                    seoModel,
                    page,
                    order,
                    pageSize,
                    stage,
                    bffUri,
                    language,
                    brand,
                    searchEndpoints: appConfig.searchEndpoints,
                    numberOfSuggestions,
                }),
                fetchSeoHeadInfos({ seoModel, order, page, pageSize, language, bffUri }),
            ]);
            const { totalCount, topPositionId, classifieds, enlargedClassifieds, classifiedsData, enrichedClassifiedsData, suggestedClassifieds, } = searchData;
            const pageProps = {
                searchModel: enrichedSearchModel,
                searchServiceModel: enrichedSearchServiceModel,
                seoModel,
                page,
                order,
                totalCount,
                topPositionId,
                pageData,
                classifieds,
                enlargedClassifieds,
                suggestedClassifieds,
                classifiedsData,
                enrichedClassifiedsData,
                linkboxes,
                breadcrumb,
                hubContent: content,
                featureFlags,
                searchIndex: 0,
                apiError: false,
                gone: false,
                isLoading: false,
                appConfig,
            };
            return {
                pageProps,
                headInfos,
            };
        }
        logger.warn('[getInitSerpData - router]: unknown use case -> ', routerProps);
    }
    catch (error) {
        if (error instanceof PaginationExceededError) {
            // do nothing
        }
        if (error instanceof Error) {
            logger.warn(`[getInitSerpData - error]: ${error.message}`, error);
        }
        else {
            logger.warn(`[getInitSerpData - unknown]`);
        }
    }
    return defaultValues;
};
